frappe.provide("ioi_partners")

// How many more-info links / related products a tile shows before the "+N more"
// control. Tune here only - both blocks share it deliberately, so the two lists
// stay visually balanced side by side.
const MAX_VISIBLE_EXTRAS = 3;
// The promotion's band positions laid out in the image column (get_media_html): under or above the image,
// and the side strips, which stay under the image where the card is too narrow for them. "price" is the
// body's (get_price_band_html).
const MEDIA_BAND_POSITIONS = ['under', 'above', 'left', 'right'];
// The bands whose title the editor may align left (band_text_align): the horizontal ones.
const ALIGNABLE_BAND_POSITIONS = ['under', 'above', 'price'];

ioi_partners.ProductList = class {
	/* Options:
		- items: Items
		- products_section: Products Wrapper
	*/
	constructor(options) {
		Object.assign(this, options);

		this.products_section.empty();
		this.make();
	}

	make() {
		let me = this;
		let html = ``;

		// For a guest the server sends in_cart: false on every item (it holds no cart
		// for them). The local guest cart is applied here because every listing,
		// searched or not, renders through this class.
		if (ioi_partners.is_guest()) {
			ioi_partners.guest_cart.mark_in_cart(this.items);
		}

		this.items.forEach(item => {
			let title = item.display_name || item.product_name || item.name || "";
			title = title.length > 200 ? title.substr(0, 200) + "..." : title;

			html += `<article class="product-tile${ me.get_promotion_classes(item) }" data-item="${item.name}"${ me.get_promotion_style(item) }>`;
			html += me.get_media_html(item, title);
			html += me.get_tile_body_html(item, title);
			html += `</article>`;
		});

		this.products_section.append(html);

		this.items.forEach(item => {
			this.bind_add_remove_qty(item);
		})
	}

	// The tile's classes beyond "product-tile", from the enabled promotion when it lists the product
	// (item.promotion, eshop/promotion.py get_card_promotions): none for any other product, whose tile keeps
	// today's markup. The tint is the image area's, or the price block's when the band is above the price
	// (get_price_band_html).
	get_promotion_classes(item) {
		const promotion = item.promotion;
		if (!promotion) {
			return '';
		}

		let classes = ' product-tile--promo';
		if (promotion.border) {
			classes += ' product-tile--promo-border';
		}
		if (promotion.tint && promotion.band_position !== 'price') {
			classes += ' product-tile--promo-tint';
		}
		if (['left', 'right'].includes(promotion.band_position)) {
			classes += ` product-tile--promo-${ promotion.band_position }`;
		}
		return classes;
	}

	// The promotion's colors as custom properties, from a color the server checked (get_promotion_style):
	// the only style attribute the promotion's markup gets, on the tile, for its band, text and tint to read.
	get_promotion_style(item) {
		if (!item.promotion) {
			return '';
		}
		return ` style="${ frappe.utils.escape_html(item.promotion.style) }"`;
	}

	// The image column. With the promotion's band under or above the image, or at a side of the card, which
	// the stylesheet lays out as a strip where the card is wide enough and leaves under the image otherwise,
	// band and image share one wrapper of the image column's height, so the titles stay level across a row.
	// Without such a band, the image column alone, as before.
	get_media_html(item, title) {
		const position = item.promotion && item.promotion.band_position;
		if (!MEDIA_BAND_POSITIONS.includes(position)) {
			return this.get_image_html(item, title);
		}

		const image = this.get_image_html(item, title);
		const band = this.get_promotion_band_html(item);
		return `<div class="product-tile-media">${ position === 'above' ? band + image : image + band }</div>`;
	}

	// The promotion's band: its title, with a "*" when the promotion has a text. In a bubble, the band is
	// the button that opens it (public/js/cart/price_rule_bubble.js, delegated on the document), as a variable
	// price's "**" is: the bubble's content, which the server built from escaped parts (get_bubble_html), is
	// registered under the product's own key, each product's holding its own price, and the button carries
	// the key alone. Otherwise a plain block, which opens nothing. The title is the editor's text: escaped,
	// never run through __(). Called for a position of MEDIA_BAND_POSITIONS or "price" only.
	get_promotion_band_html(item) {
		const promotion = item.promotion;
		const position = promotion.band_position;
		let classes = `product-tile-promo-band product-tile-promo-band--${ position }`;
		if (promotion.band_align === 'left' && ALIGNABLE_BAND_POSITIONS.includes(position)) {
			classes += ' product-tile-promo-band--align-left';
		}
		const marker = promotion.text_mode ? `<sup class="product-tile-promo-marker" aria-hidden="true">*</sup>` : '';
		const label = `<span class="product-tile-promo-band-label"${ this.get_lang_attribute(promotion.title_lang) }>`
			+ `${ frappe.utils.escape_html(promotion.title) }${ marker }</span>`;

		if (promotion.text_mode !== 'bubble') {
			return `<div class="${ classes }">${ label }</div>`;
		}

		ioi_partners.price_rule_bubble.register_rules(promotion.bubble_key, [{ html: promotion.bubble_html }], promotion.title);
		return `<button type="button" class="${ classes } promotion-trigger" aria-haspopup="dialog"
			aria-expanded="false" aria-controls="ioi-price-rule-popup"
			data-price-rule-key="${ frappe.utils.escape_html(promotion.bubble_key) }">${ label }</button>`;
	}

	// The promotion's text above the price, when the promotion shows it there rather than in a bubble: "*"
	// and the title, as the bubble starts, then the text, whose line breaks the stylesheet keeps. Before the
	// divider, and before the band when that is above the price too. Escaped, never run through __().
	get_promotion_text_html(item) {
		const promotion = item.promotion;
		if (!promotion || promotion.text_mode !== 'price') {
			return '';
		}

		return `<div class="product-tile-promo-text product-tile-promo-text--price">`
			+ `<strong class="product-tile-promo-text-key"${ this.get_lang_attribute(promotion.title_lang) }>`
			+ `*${ frappe.utils.escape_html(promotion.title) }</strong> `
			+ `<span class="product-tile-promo-text-body"${ this.get_lang_attribute(promotion.text_lang) }>`
			+ `${ frappe.utils.escape_html(promotion.text) }</span></div>`;
	}

	// The promotion's band above the price, before the divider. With the tint, it opens the tinted price
	// block, which holds the divider (hidden by the stylesheet), the price row and the button, and which
	// get_tile_body_html closes after the button (has_price_block).
	get_price_band_html(item) {
		if (!item.promotion || item.promotion.band_position !== 'price') {
			return '';
		}

		const band = this.get_promotion_band_html(item);
		return this.has_price_block(item) ? `<div class="product-tile-price-block">${ band }` : band;
	}

	has_price_block(item) {
		return !!(item.promotion && item.promotion.band_position === 'price' && item.promotion.tint);
	}

	// ' lang="en"' for an English title or text standing in for the visitor's own (the server sends a
	// language only then), "" otherwise.
	get_lang_attribute(lang) {
		return lang ? ` lang="${ frappe.utils.escape_html(lang) }"` : '';
	}

	get_image_html(item, title) {
		let image = item.image;
		let html = `<div class="product-tile-image">`;
		html += `<span class="product-tile-badge">${ __(item.product_family) || '' }</span>`;
		// decorative only — no click handler, no endpoint yet; wire up via this marker later
		html += `<span class="product-tile-wish" data-wishlist-heart="${ item.name }">${ frappe.utils.icon('heart', 'xs') }</span>`;

		let product_line = (item.product_line || '').trim();
		if (product_line) {
			html += `
				<div class="product-tile-line">
					<span class="product-tile-line-label">${ __("Product line") }</span>
					<span class="product-tile-line-value">${ frappe.utils.escape_html(product_line) }</span>
				</div>
			`;
		}

		if (image) {
			html += `
				<div class="product-link product-list-link">
					<img itemprop="image" class="website-image product-tile-img" alt="${ title }"
						src="${ image }">
				</div>
			`;
		} else {
			html += `
				<div class="product-link product-list-link"
					style="text-decoration: none">
					<div class="card-img-top no-image-list">
						${ frappe.get_abbr(title) }
					</div>
				</div>
			`;
		}

		html += `</div>`;
		return html;
	}

	get_tile_body_html(item, title) {
		let body = `<div class="product-tile-body">`;
		body += `<h3 class="product-tile-title">${ title }</h3>`;
		if (!item.available_in_eval) {
			body += this.get_eval_warning_html(item); // isolated — easy to pull/swap later
		}
		body += this.get_description_html(item);
		body += this.get_chips_html(item);
		// Above the divider, so both blocks sit in the tile's upper group and the
		// divider's margin-top:auto keeps price + CTA pinned to the card bottom.
		// Deliberately not inside get_description_html(): the detail toggle there
		// finds its list via previousElementSibling (products.js:98), so nothing
		// may be inserted between that <ul> and its button.
		body += this.get_tile_extras_html(item);
		body += this.get_promotion_text_html(item);
		body += this.get_price_band_html(item);
		body += `<div class="product-tile-divider"></div>`;
		body += this.get_price_row_html(item);

		body += `<div class="product-tile-cta">`;
		if (!item.cannot_be_selected) {
			if (item.in_cart && (["User", "Credit"].includes(item.unit_of_measure) || item.allow_quantity)) {
				body += this.get_quantity_picker(item);
				body += this.get_primary_button(item);
			} else {
				body += this.get_primary_button(item);
			}
		}
		// The legend of the price's "**", under the button whatever it reads, and for a
		// product that cannot be selected too (no button then).
		body += this.get_price_rule_html(item, __("variable price, see rules"));
		body += `</div>`;
		// The tinted price block's end (get_price_band_html): after the button, which it holds.
		if (this.has_price_block(item)) {
			body += `</div>`;
		}
		body += `</div>`;
		return body;
	}

	get_eval_warning_html(item) {
		// isolated method — pull this call out (get_tile_body_html) or swap its body
		// for a tooltip later without touching surrounding tile logic
		return `<span class="product-tile-warn">${ __("Not available in evaluation") }</span>`;
	}

	get_description_html(item) {
		const MAX_VISIBLE_ITEMS = 5; // scoped constant — tune here only

		// description_fr/en/nl/de are Quill-authored Text Editor fields. Verified live
		// (2026-07-20, sales.silicon.local, 81 published products): 4 are structured as
		// a lead-in (chapô, 1-2 <p>) followed by an <ol data-list="bullet"><li>...
		// list; 77 are a single flat paragraph with no list at all. Both shapes are
		// handled: the chapô is always shown in full, the list (if any) is capped and
		// gated behind the toggle.
		let { chapo, items } = this.get_description_parts(item.description || '');
		let html = '';

		if (chapo) {
			html += `<p class="product-tile-desc">${ chapo }</p>`;
		}

		if (items.length) {
			html += `<ul class="product-tile__detail-list">`;
			items.forEach((text, i) => {
				let hidden_class = i >= MAX_VISIBLE_ITEMS ? ' product-tile__detail-item--hidden' : '';
				html += this.get_detail_list_item_html(text, hidden_class);
			});
			html += `</ul>`;

			if (items.length > MAX_VISIBLE_ITEMS) {
				html += `
					<button type="button" class="product-tile__detail-toggle" aria-expanded="false">
						<span class="product-tile__detail-toggle-label">${ __("View Detail") }</span>
						${ frappe.utils.icon('sort', 'xs', '', '', 'product-tile__detail-chevron') }
					</button>
				`;
			}
		}

		if (item.is_voucher) {
			html += `<span class="product-tile-voucher-badge">${ __("Voucher") }</span>`;
		}

		let extra = '';
		if (item.install_cost && item.install_cost != 0) {
			extra += `<div>${__("Install cost")}: ${ item.formatted_install_cost }</div>`;
		}
		if (item.footer_note) {
			extra += `<div class="text-muted footer-note">${ __(item.footer_note) }</div>`;
		}
		if (extra) {
			html += `<div class="product-tile__secondary-info">${ extra }</div>`;
		}

		return html;
	}

	get_description_parts(html) {
		const tmp = document.createElement('div');
		tmp.innerHTML = html;

		const list_el = tmp.querySelector('ul, ol');
		if (!list_el || !list_el.parentElement) {
			// no real list structure — the whole thing is the chapô, unchanged from before
			return { chapo: tmp.textContent.trim(), items: [] };
		}

		const siblings = Array.from(list_el.parentElement.children);
		const list_index = siblings.indexOf(list_el);
		const chapo = siblings.slice(0, list_index)
			.map(el => el.textContent.trim())
			.filter(Boolean)
			.join(' ');
		// textContent naturally skips Quill's empty `<span class="ql-ui">` bullet-marker spans
		const items = Array.from(list_el.querySelectorAll('li'))
			.map(li => li.textContent.trim())
			.filter(Boolean);

		return { chapo, items };
	}

	get_detail_list_item_html(text, hidden_class = '') {
		// hand-authored inline SVG, not frappe.utils.icon() — neither available icon set
		// has a combined check-circle glyph; stroke uses currentColor so it inherits
		// var(--tile-purple) from .product-tile__detail-bullet, same convention as the
		// existing hand-authored search icon in eshop/ui/views.js's prepare_search()
		let bullet = `
			<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
				stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
				<circle cx="12" cy="12" r="10"></circle>
				<polyline points="8 12 11 15 16 9"></polyline>
			</svg>
		`;
		return `
			<li class="product-tile__detail-item${hidden_class}">
				<span class="product-tile__detail-bullet">${ bullet }</span>
				${ text }
			</li>
		`;
	}

	get_extra_hidden_class(index) {
		// File-scoped cap, same convention as MAX_VISIBLE_ITEMS in
		// get_description_html and VISIBLE_ITEM_CAP in FloatingCart.vue.
		return index >= MAX_VISIBLE_EXTRAS ? ' product-tile-extra--hidden' : '';
	}

	get_extras_toggle_html(total) {
		if (total <= MAX_VISIBLE_EXTRAS) {
			return '';
		}

		// "+{0} more" already exists in fr/nl/de - reused verbatim, no new string.
		// One-way on purpose: revealing needs no second label, whereas a collapse
		// would need a counterpart string that does not exist in the CSVs yet.
		return `
			<button type="button" class="product-tile-extra-toggle" aria-expanded="false">
				${ __("+{0} more", [total - MAX_VISIBLE_EXTRAS]) }
			</button>
		`;
	}

	get_tile_extras_html(item) {
		const more_info = this.get_more_info_html(item);
		const related = this.get_related_count_html(item);

		// Nothing at all when both are empty - no row, no empty slot. That is the
		// nominal case for most of the catalogue.
		if (!more_info && !related) {
			return '';
		}

		return `<div class="product-tile-extras-row">${ more_info }${ related }</div>`;
	}

	get_more_info_html(item) {
		const links = item.more_info || [];
		// Nothing at all when empty - no heading, no wrapper. This is the nominal
		// case: most of the catalogue carries no links.
		if (!links.length) {
			return '';
		}

		let html = `<div class="product-tile-extras product-tile-extras--links">`;
		html += `<span class="product-tile-extras-title">${ __("More info") }</span>`;
		html += `<div class="product-tile-extras-body">`;

		links.forEach((link, i) => {
			// escape_html on both, never __(): these are editor-entered values, and
			// running data through the translation pool only "works" when a CSV row
			// happens to match (see the footer_note call at line 148 below).
			// The href is additionally https-only, enforced server-side in
			// ioi sys Product.validate_more_info().
			html += `
				<a class="product-tile-link${ this.get_extra_hidden_class(i) }"
					href="${ frappe.utils.escape_html(link.url) }"
					target="_blank" rel="noopener noreferrer">
					<span class="product-tile-link-label">${ frappe.utils.escape_html(link.label) }</span>
					${ frappe.utils.icon('external-link', 'xs') }
				</a>
			`;
		});

		html += `</div>`;
		html += this.get_extras_toggle_html(links.length);
		html += `</div>`;
		return html;
	}

	get_related_count_html(item) {
		const products = item.related_products || [];
		if (!products.length) {
			return '';
		}

		// msgid is "Related products" alone, already translated in fr/nl/de; the
		// count is appended outside the lookup so no new CSV row is needed and the
		// number never ends up baked into a translated literal.
		//
		// Now a real button: it opens the shared popup, so it takes aria-expanded,
		// aria-controls and a pointer cursor. aria-controls names the single
		// body-level popup every tile reuses, which is why the id is a constant
		// rather than per-card.
		//
		// The rows travel with the button rather than being looked up on open, so
		// the popup never depends on which listing product_view last kept.
		const payload = products.map(product => ({
			name: product.name,
			product_name: product.product_name,
			display_name: product.display_name,
			image: product.image,
		}));

		return `
			<button type="button" class="product-tile-related-count"
				aria-expanded="false" aria-controls="ioi-related-popup"
				data-related="${ frappe.utils.escape_html(JSON.stringify(payload)) }">
				${ __("Related products") } (${ products.length })
			</button>
		`;
	}

	get_chips_html(item) {
		let html = `<div class="product-tile-meta">`;
		if (item.estimated_setup_time && item.estimated_setup_time != 0) {
			html += `<span class="product-chip">${ frappe.utils.icon('es-line-time', 'xs') }~${ item.estimated_setup_time }h ${__("config")}</span>`;
		}
		html += `<span class="product-chip product-chip-code">${ item.name }</span>`;
		html += `</div>`;
		return html;
	}

	get_price_row_html(item) {
		if (item.formatted_striked_price) {
			return this.get_struck_price_row_html(item);
		}

		let html = `<div class="product-tile-price-row">`;
		html += `<span class="product-tile-price-amount">${ item.formatted_price || '' }${ this.get_price_rule_html(item) }</span>`;
		if (item.pricing_unit) {
			html += `<span class="product-tile-price-pill">${ __(item.pricing_unit) }</span>`;
		}
		html += `</div>`;
		return html;
	}

	// The row of a product with its own struck price (ioi sys Product.striked_price, which eshop/query.py
	// formats when above 0), with or without a promotion: the current price and the struck price grouped,
	// then the pill as get_price_row_html renders it. The stylesheet keeps the struck price right of the
	// current price, moves it above when the two do not fit side by side, and wraps the pill above them
	// first. Both amounts are the server's formatted prices; a screen reader hears which one is the old one.
	get_struck_price_row_html(item) {
		let html = `<div class="product-tile-price-row product-tile-price-row--was">`;
		html += `<span class="product-tile-price-group">`;
		html += `<span class="product-tile-price-amount">${ item.formatted_price || '' }${ this.get_price_rule_html(item) }</span>`;
		html += `<del class="product-tile-price-was"><span class="sr-only">${ __("Previous price") } </span>${ item.formatted_striked_price }</del>`;
		html += `</span>`;
		if (item.pricing_unit) {
			html += `<span class="product-tile-price-pill">${ __(item.pricing_unit) }</span>`;
		}
		html += `</div>`;
		return html;
	}

	// A variable product's "**", alone (after the price) or before text (the note
	// under the button), as PriceRuleTrigger.vue renders it on a cart line: a
	// button opening its rule's bubble (public/js/cart/price_rule_bubble.js,
	// delegated on the document) when the product has a rule, plain text
	// otherwise. The rule is the server's cleaned HTML (query.py add_price_rules),
	// registered with the bubble under the product: the button carries the key
	// alone. The "**" stays out of the msgid.
	get_price_rule_html(item, text = '') {
		if (!item.variable_price) {
			return '';
		}

		const label = text ? ` ${ frappe.utils.escape_html(text) }` : '';
		const note_class = text ? ' price-rule-note' : '';
		if (!item.price_rule) {
			return `<span class="price-rule-static${ note_class }"><sup class="total-note-marker">**</sup>${ label }</span>`;
		}

		const key = `product:${ item.name }`;
		ioi_partners.price_rule_bubble.register_rules(key, [{ html: item.price_rule, lang: item.price_rule_lang }]);
		// The price's "**" is out of the tab order and hidden from screen readers: the
		// note under the button, which opens the same bubble, is the tile's control.
		const marker_attributes = text ? '' : ' tabindex="-1" aria-hidden="true"';
		const aria_hidden = text ? ' aria-hidden="true"' : '';
		return `<button type="button" class="price-rule-trigger${ note_class }" aria-haspopup="dialog"
			aria-expanded="false" aria-controls="ioi-price-rule-popup"${ marker_attributes }
			data-price-rule-key="${ frappe.utils.escape_html(key) }"><sup class="total-note-marker"${ aria_hidden }>**</sup>${ label }</button>`;
	}

	get_primary_button(item) {
		return `
			<div id="${ item.name }" class="btn
				btn-sm btn-primary btn-add-to-cart-list mb-0 ${item.in_cart ? 'btn-go-to-cart-list' :''}"
				data-item-code="${ item.name }" data-selection-option="${ item.selection_option }" data-product-family="${ item.product_family }">
				<span class="cart-indicator ${item.in_cart ? '' :'hidden'}">
					${frappe.utils.icon('check', 'xs')}
				</span>
				<span class="btn-add-to-cart-list-icon ${item.in_cart ? 'hidden' :''}">
					<svg class="icon icon-md">
						<use href="#icon-assets"></use>
					</svg>
				</span>
				<span class="add-to-cart-btn-text ${item.in_cart ? 'hidden' : 'd-none d-sm-inline'}">${ __('Add to Cart') }</span>
				<span class="go-to-cart ${item.in_cart ? 'd-none d-sm-inline' : 'hidden'}">${(["User", "Credit"].includes(item.unit_of_measure) || item.allow_quantity) ? item.total_in_cart + " " + __("in basket") : __("See selection") }</span>
			</div>
		`;
	}

	get_quantity_picker(item) {
		const picker = `<div class="flex">
				<div class="qty-minus grey-btn"><span>-</span></div>
				<div class="qty">${item.total_in_cart} ${__(item.unit_of_measure)}</div>
				<div class="qty-plus grey-btn"><span>+</span></div>
			</div>`

		this.bind_add_remove_qty(item)

		return picker
	}

	bind_add_remove_qty(item) {
		$(`#product-listing [data-item='${item.name}']`).find(".qty-minus").click(e => {
			this.product_listing.remove_qty_from_subscription(item.name)
		})

		$(`#product-listing [data-item='${item.name}']`).find(".qty-plus").click(() => {
			this.product_listing.add_qty_to_subscription(item.name)
		})
	}

};